import React, { useState } from "react";
import ActionMenu from "components/ActionMenu";
import { useTranslation } from "react-i18next";
import { IconChevronDown } from "@tabler/icons-react";
import DGNBModal from "features/Building/BuildingSourceFiles/DGNB/DGNBModal";
import CertificationExporter from "features/CertificationExport";
import { buildingStore } from "store/Building/BuildingStore";
import { observer } from "mobx-react-lite";
import DGNBItem from "./DGNBItem";
import CertificationExporterItem from "./CertificationExporterItem";
import Tooltip from "components/Tooltip";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { LicenseFeatureEnum } from "api-client";
import ExportPDF from "./ExportPDF";
import ExportToolMenu from "./MasterToolMenu";
import MasterToolExportProgressModal from "./MasterToolProgressModal";
import GeneratePDFProgressModal from "./GeneratePDFProgressModal";
import { usePostHog } from "@posthog/react";

export default observer(function ExportMenu() {
  const { t } = useTranslation();
  const [openDGNBPass, setOpenDGNBPass] = useState(false);
  const [openCertificateExport, setOpenCertificateExport] = useState(false);
  const { hasFeature } = useFeatureAccess();
  const [openGenaratePDFModal, setOpenGenaratePDFModal] = React.useState(false);
  const [openExportTool, setOpenExportTool] = React.useState(false);
  const { buildingMappingStatus } = buildingStore;
  const posthog = usePostHog();

  const hasExportLicense = hasFeature(
    LicenseFeatureEnum.ButtonLcaGrpExportsEnabled
  );
  const hasExportMasterToolLicense = hasFeature("export_master_tool");

  const isExportDisabled =
    buildingMappingStatus.hasReport === false || !hasExportLicense;

  const isPDFDisabled = !hasFeature(LicenseFeatureEnum.PdfReportExports);

  const isDGNBExportDisabled = !hasFeature(
    LicenseFeatureEnum.ExportDgnbGebauderessourcenpass
  );

  const isEnvironmentalExportDisabled = !hasFeature(
    LicenseFeatureEnum.ExportEnvironmentalReport
  );

  const captureExportStarted = (exportType: string) => {
    posthog.capture("export_started", {
      export_type: exportType,
      building_id: buildingStore.currentBuilding?.id,
      building_name: buildingStore.currentBuilding?.name,
    });
  };

  const menuItems = [
    {
      component: <DGNBItem key="dgnb" disabled={isDGNBExportDisabled} />,
      disabled: isDGNBExportDisabled,
      action: () => {
        captureExportStarted("dgnb");
        setOpenDGNBPass(true);
      },
      visible: true,
    },
    {
      component: (
        <CertificationExporterItem
          key="cert"
          disabled={isEnvironmentalExportDisabled}
        />
      ),
      disabled: isEnvironmentalExportDisabled,
      action: () => {
        captureExportStarted("certification");
        setOpenCertificateExport(true);
      },
      visible: true,
    },
    {
      component: <ExportPDF key="pdf" disabled={isPDFDisabled} />,
      disabled: isPDFDisabled,
      action: () => {
        captureExportStarted("pdf");
        setOpenGenaratePDFModal(true);
      },
      visible: true,
    },
    {
      component: <ExportToolMenu key="master-tool" />,
      disabled: false,
      action: () => {
        captureExportStarted("master_tool");
        setOpenExportTool(true);
      },
      visible: hasExportMasterToolLicense,
    },
  ].filter((item) => item.visible);

  const items = menuItems.map((item) => item.component);
  const itemsDisabled = menuItems.map((item) => item.disabled);

  const exportMenu = () => {
    return (
      <ActionMenu
        itemDisabled={itemsDisabled}
        items={items}
        onSelect={(index) => menuItems[index].action()}
        disabled={isExportDisabled}
        icon={
          <div
            aria-disabled={true}
            className="h-10 flex flex-row cursor-pointer shadow-sm mr-4 group/menuButton"
          >
            <div className="h-full px-2 flex flex-row  bg-indigo-600 group-hover/menuButton:bg-indigo-700 rounded-bl-md rounded-tl-md items-center">
              <p className="text-white ml-2 font-medium text-base pr-1">
                {t("DGNB.export")}
              </p>
            </div>
            <IconChevronDown
              color="white"
              className="px-2 h-full ml-0.5 bg-indigo-600 rounded-br-md group-hover/menuButton:bg-indigo-700 rounded-tr-md w-9"
            />
          </div>
        }
        itemsClassName="whitespace-nowrap !top-10 shadow-sm right-4"
        testID="export_button"
      />
    );
  };

  return (
    <>
      {isExportDisabled ? (
        <Tooltip
          content={
            !hasExportLicense
              ? t("buildingMaterialResources.exportsNoLicenseTooltip")
              : t("buildingPassport.exportDisabledTooltip")
          }
        >
          {exportMenu()}
        </Tooltip>
      ) : (
        exportMenu()
      )}
      {openDGNBPass && (
        <DGNBModal open={openDGNBPass} setOpen={setOpenDGNBPass} />
      )}
      <CertificationExporter
        open={openCertificateExport}
        setOpen={setOpenCertificateExport}
      />
      {openGenaratePDFModal && (
        <GeneratePDFProgressModal
          open={openGenaratePDFModal}
          close={() => setOpenGenaratePDFModal(false)}
        />
      )}
      {openExportTool && (
        <MasterToolExportProgressModal
          open={openExportTool}
          close={() => setOpenExportTool(false)}
        />
      )}
    </>
  );
});
